'use client';

import { CSSProperties, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  Braces,
  Move3D,
  Sliders,
  Repeat,
  Route,
  Code2,
  BookOpenCheck,
  ExternalLink,
} from 'lucide-react';
import StandardLayout from '../../components/layout/StandardLayout';
import CodeExample from '../../components/ui/CodeExample';
import { useSite } from '@/lib/site-context';
import '../../components/editor/Documentation.scss';
import TableOfContents, { TocSection } from '../../components/ui/TableOfContents';
import apiData from './generated/fullcontrol-api.json';

type ParameterSpec = {
  name: string;
  description: string;
  required?: boolean;
  defaultValue?: string;
};

type ApiSpec = {
  name: string;
  summary: string;
  params: ParameterSpec[];
  returns?: string;
};

type ApiGroup = {
  title: string;
  description: string;
  apis: string[];
};

type GeneratedParam = {
  name: string;
  description?: string;
  required?: boolean;
  default?: string;
};

type GeneratedDocEntry = {
  name: string;
  module: string;
  kind: string;
  summary?: string;
  docstring?: string;
  parameters?: GeneratedParam[];
  returns?: { description?: string } | null;
};

const generatedDocs = new Map<string, GeneratedDocEntry>(
  (apiData as GeneratedDocEntry[]).map((entry) => [entry.name, entry])
);

type ManualOverride = Partial<ApiSpec>;

const manualOverrides: Record<string, ManualOverride> = {
  Point: {
    summary:
      'Single XYZ position with optional metadata like extrusion state, per-move speed, and viewer color overrides.',
    params: [
      { name: 'x', description: 'X coordinate in millimeters relative to the printer origin.', defaultValue: '0' },
      { name: 'y', description: 'Y coordinate in millimeters.', defaultValue: '0' },
      { name: 'z', description: 'Z height in millimeters (layer height or custom toolpath elevation).', defaultValue: '0' },
      { name: 'extrude', description: 'Set true to treat this move as an extrusion segment.', defaultValue: 'false' },
      {
        name: 'speed',
        description: 'Optional override in mm/s for this move (otherwise inherits the active print/travel speed).',
        defaultValue: 'inherits active speed',
      },
      {
        name: 'color',
        description: 'Optional [r, g, b] tuple rendered when PlotControls.color_type is set to manual.',
        defaultValue: 'auto',
      },
    ],
    returns: 'Point instance ready to append to steps or feed into geometry helpers.',
  },
  Vector: {
    summary: 'Direction + magnitude helper used by move(), ramp helpers, and polar utilities.',
    params: [
      { name: 'x', description: 'X component of the vector.', defaultValue: '0' },
      { name: 'y', description: 'Y component of the vector.', defaultValue: '0' },
      { name: 'z', description: 'Z component of the vector.', defaultValue: '0' },
    ],
    returns: 'Vector that can be added to Points or reused across multiple transforms.',
  },
  ExtrusionGeometry: {
    summary: 'Defines the bead cross-section so downstream transforms can compute flow and visualization.',
    params: [
      {
        name: 'area_model',
        description: "One of 'rectangle', 'stadium', 'circle', or 'manual'. Determines which other fields are read.",
        defaultValue: 'rectangle',
      },
      { name: 'width', description: 'Line width in millimeters (rectangle + stadium).', defaultValue: 'printer default' },
      { name: 'height', description: 'Layer height in millimeters (rectangle + stadium).', defaultValue: 'printer default' },
      { name: 'diameter', description: 'Cross-section diameter in millimeters (circle model).', defaultValue: 'line width' },
      { name: 'area', description: 'Manual area override in mm² when area_model = manual.', defaultValue: 'calculated automatically' },
    ],
    returns: 'ExtrusionGeometry object referenced by fc.transform and viewer pipelines.',
  },
  Extruder: {
    summary: 'Turns extrusion on/off and configures filament properties.',
    params: [
      { name: 'on', description: 'True enables extrusion, false triggers a travel move.', defaultValue: 'true' },
      { name: 'units', description: "'mm' for filament length or 'mm3' for volume-based flow.", defaultValue: 'mm' },
      { name: 'dia_feed', description: 'Filament diameter used for volume conversion.', defaultValue: '1.75' },
      { name: 'relative_gcode', description: 'Emit relative extrusion commands when true.', defaultValue: 'false' },
      { name: 'retraction_length', description: 'Default retraction length in mm.', defaultValue: 'profile default' },
      { name: 'retraction_speed', description: 'Default retraction speed in mm/s.', defaultValue: 'profile default' },
    ],
    returns: 'Extruder state change appended to steps.',
  },
  Retraction: {
    summary: 'Overrides the next retraction event with custom values.',
    params: [
      { name: 'length', description: 'Retraction distance in mm (falls back to Extruder defaults).', defaultValue: 'extruder default' },
      { name: 'speed', description: 'Retraction speed mm/s.', defaultValue: 'extruder default' },
    ],
    returns: 'Command that retracts filament at the specified settings.',
  },
  Unretraction: {
    summary: 'Explicit unretraction helper mirroring Retraction parameters.',
    params: [
      { name: 'length', description: 'Unretraction distance in mm.', defaultValue: 'extruder default' },
      { name: 'speed', description: 'Unretraction speed mm/s.', defaultValue: 'extruder default' },
    ],
    returns: 'Command that restores filament flow after a retract.',
  },
  ExtrusionRate: {
    summary: 'Scales the requested flow rate without editing geometry.',
    params: [{ name: 'rate', description: 'Multiplier applied to the nominal flow.', defaultValue: '1.0' }],
    returns: 'Extrusion rate change affecting subsequent moves.',
  },
  StationaryExtrusion: {
    summary: 'Deposits material without moving (ooze shields, blobs, primers).',
    params: [
      { name: 'volume', description: 'Volume or filament length to extrude.', required: true },
      { name: 'speed', description: 'Extrusion speed mm/s.', defaultValue: '5' },
    ],
    returns: 'Extrusion command at the current position.',
  },
  PrintSpeed: {
    summary: 'Sets the speed for extrusion moves.',
    params: [{ name: 'speed', description: 'Print speed in mm/s.', required: true }],
    returns: 'Print speed override until changed again.',
  },
  TravelSpeed: {
    summary: 'Sets the speed for travel (non-extruding) moves.',
    params: [{ name: 'speed', description: 'Travel speed in mm/s.', required: true }],
    returns: 'Travel speed override for future moves.',
  },
  Hotend: {
    summary: 'Controls nozzle temperature and optional wait behavior.',
    params: [
      { name: 'temp', description: 'Target temperature in deg C.', required: true },
      { name: 'wait', description: 'When true, waits until the target temp is reached before continuing.', defaultValue: 'true' },
      { name: 'tool', description: 'Tool index for multi-extruder printers.', defaultValue: '0' },
    ],
    returns: 'Temperature command inserted into the G-code stream.',
  },
  Buildplate: {
    summary: 'Sets bed temperature.',
    params: [
      { name: 'temp', description: 'Target temperature in deg C.', required: true },
      { name: 'wait', description: 'Wait for the bed before continuing.', defaultValue: 'true' },
    ],
    returns: 'Bed temperature command.',
  },
  Fan: {
    summary: 'Controls part cooling fan speed.',
    params: [{ name: 'speed_percent', description: 'Fan speed from 0-100%.', defaultValue: '100' }],
    returns: 'Fan speed command.',
  },
  GcodeControls: {
    summary: 'Sets printer name, header info, and export behavior.',
    params: [
      { name: 'printer_name', description: 'Device profile name (matches fullcontrol profiles).', defaultValue: 'generic' },
      {
        name: 'initialization_data',
        description: 'Object of values injected into the printer profile (temps, primer selection, etc.).',
        defaultValue: '{}',
      },
      { name: 'save_as', description: 'Filename used when downloading G-code.', defaultValue: 'sketch.gcode' },
      { name: 'include_date', description: 'Adds a timestamp comment to the file header.', defaultValue: 'true' },
      { name: 'show_banner', description: 'Add the FullControl banner to the output.', defaultValue: 'true' },
      { name: 'show_tips', description: 'Include printing tips in comments.', defaultValue: 'true' },
      { name: 'silent', description: 'Suppress console warnings during transforms.', defaultValue: 'false' },
    ],
    returns: 'Controls object passed to fc.gcode or fc.transform.',
  },
  PlotControls: {
    summary: 'Tweaks how the Plotly viewer displays geometry.',
    params: [
      {
        name: 'color_type',
        description: "'z_gradient', 'print_sequence', 'print_sequence_fluctuating', 'random_blue', or 'manual'.",
        defaultValue: 'z_gradient',
      },
      { name: 'style', description: "'tube' for shaded extrusions or 'line' for polylines.", defaultValue: 'tube' },
      { name: 'hide_travel', description: 'Hide gray travel moves.', defaultValue: 'false' },
      { name: 'hide_axes', description: 'Hide XYZ axes.', defaultValue: 'false' },
    ],
    returns: 'Viewer config object consumed by the editor visualizer.',
  },
  PlotAnnotation: {
    summary: 'Adds labels to specific coordinates in the viewer.',
    params: [
      { name: 'point', description: 'Point to annotate.', required: true },
      { name: 'label', description: 'Text shown in the viewer.', required: true },
    ],
    returns: 'Annotation entry layered on top of the viewer.',
  },
  PrinterCommand: {
    summary: 'Injects a device-specific command from the printer profile.',
    params: [{ name: 'id', description: 'Key referencing the command (e.g., home, purge).', required: true }],
    returns: 'Command resolved to G-code during export.',
  },
  ManualGcode: {
    summary: 'Allows raw G-code snippets inside your design.',
    params: [{ name: 'text', description: 'Exact G-code lines to insert.', required: true }],
    returns: 'G-code lines inserted verbatim.',
  },
  GcodeComment: {
    summary: 'Adds a human-readable comment to the export.',
    params: [{ name: 'text', description: 'Comment body.', required: true }],
    returns: 'Comment line prefixed with ; in the G-code output.',
  },
  relative_move: {
    summary: 'Convenience helper that offsets a point or last point in a list by a vector.',
    params: [
      { name: 'point_or_steps', description: 'Single point or array whose last point will be used.', required: true },
      { name: 'vector', description: 'Vector describing the delta to apply.', required: true },
    ],
    returns: 'New point after the relative move.',
  },
};

const coreObjectNames = ['Point', 'Vector', 'ExtrusionGeometry'];

const geometryGroups: ApiGroup[] = [
  {
    title: 'Shapes & Paths',
    description: 'High-level constructors that emit complete loops or paths on a constant Z plane.',
    apis: ['rectangleXY', 'circleXY', 'ellipseXY', 'polygonXY', 'spiralXY', 'helixZ'],
  },
  {
    title: 'Arcs & Segments',
    description: 'Precise arc helpers plus segmentation utilities for custom resolution.',
    apis: ['arcXY', 'arcXY_3pt', 'variable_arcXY', 'segmented_line', 'segmented_path'],
  },
  {
    title: 'Movement Helpers',
    description: 'Travel and positioning tools used to stitch geometry together.',
    apis: ['travel_to', 'move', 'move_polar', 'relative_move', 'polar_to_point', 'point_to_polar'],
  },
  {
    title: 'Waves & Patterns',
    description: 'Programmatic infill utilities and coordinate ramps.',
    apis: ['squarewaveXY', 'trianglewaveXY', 'sinewaveXY', 'ramp_xyz', 'ramp_polar'],
  },
];

const controlGroups: ApiGroup[] = [
  {
    title: 'Flow & Extrusion',
    description: 'Stateful objects inserted into the steps list to change how upcoming moves behave.',
    apis: ['Extruder', 'Retraction', 'Unretraction', 'ExtrusionRate', 'StationaryExtrusion'],
  },
  {
    title: 'Speeds & Environment',
    description: 'Adjust velocities, temperature, and cooling mid-print.',
    apis: ['PrintSpeed', 'TravelSpeed', 'Hotend', 'Buildplate', 'Fan'],
  },
  {
    title: 'Runtime Metadata',
    description: 'Metadata that shapes visualization, exported G-code, and printer profiles.',
    apis: ['GcodeControls', 'PlotControls', 'PlotAnnotation', 'PrinterCommand', 'ManualGcode', 'GcodeComment'],
  },
];

const utilitySections: ApiGroup[] = [
  {
    title: 'Analysis & Cleanup',
    description: 'Helpers that validate and normalize your steps array.',
    apis: ['check', 'fix', 'flatten', 'points_only'],
  },
  {
    title: 'Math Helpers',
    description: 'Quick geometry calculations you can reuse across sketches.',
    apis: ['distance', 'angleXY_between_3_points', 'path_length', 'linspace', 'midpoint', 'centreXY_3pt'],
  },
  {
    title: 'State & I/O',
    description: 'Share data across steps or persist designs to disk.',
    apis: [
      'relative_point',
      'first_point',
      'last_point',
      'export_design',
      'import_design',
      'build_default_registry',
      'import_printer',
    ],
  },
];

const pipelineFunctions = ['gcode', 'visualize', 'transform'];

function resolveApiSpec(name: string): ApiSpec | null {
  const generated = generatedDocs.get(name);
  const manual = manualOverrides[name];

  if (!generated && !manual) {
    return null;
  }

  const manualParams = manual?.params ?? [];
  const generatedParams: ParameterSpec[] =
    generated?.parameters?.map((param) => {
      const manualParam = manualParams.find((p) => p.name === param.name);
      const inferredRequired =
        manualParam?.required ?? param.required ?? (param.default === undefined || param.default === null);
      return {
        name: param.name,
        description: manualParam?.description || param.description || '',
        required: inferredRequired,
        defaultValue: manualParam?.defaultValue ?? param.default ?? undefined,
      } as ParameterSpec;
    }) ?? [];

  const manualOnlyParams = manualParams.filter(
    (manualParam) => !generatedParams.some((param) => param.name === manualParam.name)
  );

  let params: ParameterSpec[] = [];
  if (generatedParams.length) {
    params = [...generatedParams, ...manualOnlyParams];
  } else if (manualParams.length) {
    params = manualParams;
  }

  return {
    name,
    summary: manual?.summary ?? generated?.summary ?? 'Documentation coming soon.',
    params,
    returns: manual?.returns ?? generated?.returns?.description,
  };
}

function ParamList({ params }: { params: ParameterSpec[] }) {
  if (!params.length) {
    return <p className="param-note">No parameters.</p>;
  }

  return (
    <ul className="param-list">
      {params.map((param) => {
        const metaParts: string[] = [];
        if (!param.required) {
          metaParts.push('optional');
        }
        if (param.defaultValue !== undefined) {
          metaParts.push(`default: ${param.defaultValue}`);
        }
        const meta = metaParts.length ? `(${metaParts.join(', ')})` : '';
        const descriptionSuffix = param.description ? ` – ${param.description}` : '';

        return (
          <li key={param.name}>
            <code>{param.name}</code> {meta} {descriptionSuffix}
          </li>
        );
      })}
    </ul>
  );
}

type HeadingTag = 'h3' | 'h4';

function ApiDetailsBlock({
  name,
  heading = 'h4',
  wrapperClass = 'api-entry',
}: {
  name: string;
  heading?: HeadingTag;
  wrapperClass?: string;
}) {
  const spec = resolveApiSpec(name);
  const HeadingTag = heading;

  if (!spec) {
    return (
      <div className={wrapperClass}>
        <HeadingTag>
          <code>{`fc.${name}`}</code>
        </HeadingTag>
        <p>Documentation not yet available for this API.</p>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <HeadingTag>
        <code>{`fc.${spec.name}`}</code>
      </HeadingTag>
      <p>{spec.summary}</p>
      <ParamList params={spec.params ?? []} />
      {spec.returns && (
        <p className="return-note">
          <strong>Returns:</strong> {spec.returns}
        </p>
      )}
    </div>
  );
}

export default function LibraryReferencePage() {
  const site = useSite();
  const isPython = site.id === 'py';
  const libraryName = isPython ? 'FullControl' : 'fullcontrol-js';
  const runtimeName = isPython ? 'Pyodide (CPython)' : 'a dedicated Web Worker';
  const sidebarContainerRef = useRef<HTMLDivElement>(null);
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const [sidebarStyle, setSidebarStyle] = useState<CSSProperties>({});
  const stickyOffset = 120;
  const tocSections: TocSection[] = [
    { id: 'runtime-scaffold', label: 'Runtime Scaffold' },
    { id: 'core-building-blocks', label: 'Core Building Blocks' },
    { id: 'geometry-movement', label: 'Geometry & Movement' },
    { id: 'printer-controls', label: 'Printer Controls & States' },
    { id: 'utility-helpers', label: 'Utility Helpers' },
    { id: 'pipeline-outputs', label: 'Pipeline Outputs' },
    { id: 'further-reading', label: 'Further Reading' },
  ];
  const importSnippet = isPython
    ? `import fullcontrol as fc\nimport plotly.graph_objects as go\nimport math\n\nprinter_name = 'generic'\nprinter_settings = {\n    'primer': 'travel',\n    'print_speed': 40*60,\n    'travel_speed': 80*60,\n    'nozzle_temp': 210,\n    'bed_temp': 50,\n    'fan_percent': 100,\n    'extrusion_width': 0.4,\n    'extrusion_height': 0.2\n}\n\nsteps = []\n\n# ... user code runs here ...\n\nflat_steps = fc.flatten(steps)\n# ... auto-generates plot + gcode ...`
    : `// FullControl JS scaffold (auto-generated)\nlet printer_name = 'generic';\nlet printer_settings = {\n  primer: 'travel',\n  print_speed: 2400,\n  travel_speed: 4800,\n  nozzle_temp: 210,\n  bed_temp: 50,\n  fan_percent: 100,\n  extrusion_width: 0.4,\n  extrusion_height: 0.2,\n};\nlet steps = [];\n\n// ... user code runs here ...\n\n// ... auto-generates plot + gcode ...`;

  // JS fallback ensures sticky navigation still works on browsers where CSS position: sticky is unreliable (e.g. Firefox inside flex columns).
  useEffect(() => {
    let frame: number | null = null;

    const updateSidebar = () => {
      frame = null;
      const container = sidebarContainerRef.current;
      const content = sidebarContentRef.current;
      if (!container || !content) {
        return;
      }

      const isNarrowViewport = window.innerWidth < 900;
      if (isNarrowViewport) {
        container.style.minHeight = '';
        setSidebarStyle({});
        return;
      }

      const containerTop = container.getBoundingClientRect().top;
      const shouldFix = containerTop <= stickyOffset;

      if (shouldFix) {
        const width = container.offsetWidth;
        const maxHeight = Math.max(window.innerHeight - stickyOffset - 20, 200);
        container.style.minHeight = `${content.offsetHeight}px`;
        setSidebarStyle({
          position: 'fixed',
          top: `${stickyOffset}px`,
          width: `${width}px`,
          maxHeight: `${maxHeight}px`,
          overflowY: 'auto',
        });
      } else {
        container.style.minHeight = '';
        setSidebarStyle({});
      }
    };

    const requestUpdate = () => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(updateSidebar);
    };

    requestUpdate();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [stickyOffset]);

  return (
    <StandardLayout>
      <div className="content-wrapper">
        <div className="docs-container">
          <div className="docs-layout">
            <aside className="docs-sidebar" ref={sidebarContainerRef}>
              <div className="docs-sidebar__inner" ref={sidebarContentRef} style={sidebarStyle}>
                <TableOfContents sections={tocSections} />
              </div>
            </aside>
            <div className="docs-main">
              <nav className="docs-nav">
                <Link href="/docs" className="back-link">
                  <ArrowLeft size={16} />
                  Back to Documentation
                </Link>
              </nav>

              <header className="docs-header">
                <h1>{libraryName} API Reference</h1>
                <p>
                  Practical summary of the classes and functions you call from the {site.displayName} editor.
                  Everything you run executes inside {runtimeName}, so you never install anything locally.
                </p>
              </header>

              <div className="docs-content">
                <section className="docs-section" id="runtime-scaffold">
                  <h2>Runtime Scaffold</h2>
                  <p>
                    Every sketch loads a scaffold before your code runs. It injects <code>steps</code>, printer defaults,
                    and the {libraryName} module so you can focus purely on geometry and controls.
                  </p>
                  <CodeExample language={isPython ? 'python' : 'javascript'} code={importSnippet} title="Runtime Scaffold" />
                  <div className="tip-box">
                    <h4>💡 Variant Note</h4>
                    <p>
                      py2g uses fullcontrol. js2g uses fullcontrol-js. API names stay the same, so switching between variants is trivial.
                    </p>
                  </div>
                </section>

                <section className="docs-section" id="core-building-blocks">
                  <div className="section-header">
                    <div>
                      <h2><Braces /> Core Building Blocks</h2>
                      <p>Objects you will instantiate in almost every sketch.</p>
                    </div>
                  </div>
                  <div className="concept-grid">
                    {coreObjectNames.map((name) => (
                      <ApiDetailsBlock key={name} name={name} heading="h3" wrapperClass="concept-card" />
                    ))}
                  </div>
                </section>

                <section className="docs-section" id="geometry-movement">
                  <div className="section-header">
                    <div>
                      <h2><Move3D /> Geometry & Movement</h2>
                      <p>Mix these helpers to build precise toolpaths without manual coordinate crunching.</p>
                    </div>
                  </div>
                  <div className="function-grid">
                    {geometryGroups.map((group) => (
                      <div className="function-card" key={group.title}>
                        <h3>{group.title}</h3>
                        <p>{group.description}</p>
                        {group.apis.map((apiName) => (
                          <ApiDetailsBlock key={apiName} name={apiName} />
                        ))}
                      </div>
                    ))}
                  </div>
                </section>

                <section className="docs-section" id="printer-controls">
                  <div className="section-header">
                    <h2><Sliders /> Printer Controls & States</h2>
                    <p>Insert these steps anywhere to change printer behavior mid-design.</p>
                  </div>
                  <div className="function-grid">
                    {controlGroups.map((group) => (
                      <div className="function-card" key={group.title}>
                        <h3>{group.title}</h3>
                        <p>{group.description}</p>
                        {group.apis.map((apiName) => (
                          <ApiDetailsBlock key={apiName} name={apiName} />
                        ))}
                      </div>
                    ))}
                  </div>
                </section>

                <section className="docs-section" id="utility-helpers">
                  <div className="section-header">
                    <div>
                      <h2><Repeat /> Utility Helpers</h2>
                      <p>Speed up analysis, math, and import/export flows.</p>
                    </div>
                  </div>
                  <div className="utility-grid">
                    {utilitySections.map((group) => (
                      <div className="utility-card" key={group.title}>
                        <h3>{group.title}</h3>
                        <p>{group.description}</p>
                        {group.apis.map((apiName) => (
                          <ApiDetailsBlock key={apiName} name={apiName} />
                        ))}
                      </div>
                    ))}
                  </div>
                </section>

                <section className="docs-section" id="pipeline-outputs">
                  <div className="section-header">
                    <div>
                      <h2><Route /> Pipeline Outputs</h2>
                      <p>These helpers convert your <code>steps</code> list into something tangible.</p>
                    </div>
                  </div>
                  <div className="pipeline-list">
                    {pipelineFunctions.map((name) => (
                      <ApiDetailsBlock key={name} name={name} heading="h3" wrapperClass="pipeline-card" />
                    ))}
                  </div>
                </section>

                <section className="docs-section" id="further-reading">
                  <div className="section-header">
                    <div>
                      <h2><BookOpenCheck /> Further Reading</h2>
                      <p>Dive into the upstream docs for exhaustive API details.</p>
                    </div>
                  </div>
                  <div className="external-resources">
                    <a
                      href="https://fullcontrol.xyz/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="resource-link primary-resource"
                    >
                      <div className="resource-content">
                        <span className="resource-title">Official FullControl Documentation</span>
                        <span className="resource-description">Complete API reference, tutorials, and printer profiles</span>
                      </div>
                      <ExternalLink size={16} />
                    </a>
                    <a
                      href="https://github.com/FullControlXYZ/fullcontrol"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="resource-link"
                    >
                      <div className="resource-content">
                        <span className="resource-title">GitHub Repository</span>
                        <span className="resource-description">Source code, issues, and contribution guides</span>
                      </div>
                      <Code2 size={16} />
                    </a>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StandardLayout>
  );
}
